import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  Target,
  BarChart3,
} from "lucide-react";
import Navbar from "../../../components/Navbar";
import Footer from "../../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const ISO22301_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <ShieldCheck size={18} className="text-orange-400" />
            <span>ISO Lead Auditor Training</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            ISO 22301 Certification
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Business Continuity Management System (BCMS) — International
            standard for building organizational resilience against disruptions.
          </p>
        </motion.div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              ISO 22301 Certification Overview
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-4">
              ISO 22301 is the international standard for Business Continuity
              Management Systems (BCMS). It provides organizations with a
              comprehensive framework for establishing, implementing, operating,
              monitoring, reviewing, and improving a business continuity
              management system. ISO 22301 helps businesses prepare for
              potential disruptions by identifying risks and ensuring they can
              continue to operate or recover quickly during adverse events, such
              as natural disasters, cyberattacks, or operational failures.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              Achieving ISO 22301 certification demonstrates that an
              organization has taken a proactive and structured approach to
              managing business continuity risks, ensuring that critical
              business operations can be sustained or restored rapidly in the
              event of unforeseen disruptions. It is essential for organizations
              seeking to enhance their resilience, protect their reputation, and
              maintain customer confidence during crises.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is ISO 22301 Certification?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              ISO 22301 certification is a recognized international standard
              that outlines the requirements for establishing a Business
              Continuity Management System (BCMS). The standard provides a
              structured approach to help organizations identify, assess, and
              mitigate risks to business continuity, while ensuring that
              essential functions can continue or recover quickly in case of
              disruptions.
            </p>
            <p className="text-gray-700 font-semibold text-lg mb-4">
              ISO 22301 requires organizations to:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Identify critical business functions and their dependencies.",
                "Assess potential risks and disruptions that could impact operations.",
                "Develop business continuity plans (BCPs) to ensure quick recovery.",
                "Implement preventive measures and mitigation strategies to minimize the impact of disruptions.",
                "Continuously monitor, review, and improve the BCMS.",
              ].map((text, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <CheckCircle
                    size={22}
                    className="text-[var(--bg-primary)] shrink-0"
                  />
                  <span className="text-gray-700 font-medium">{text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Key Features of ISO 22301 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: ShieldCheck,
                title: "Business Continuity Framework",
                desc: "ISO 22301 provides a systematic framework for developing a Business Continuity Management System (BCMS), covering all aspects of business continuity, from risk assessment and strategy development to implementation and recovery.",
              },
              {
                icon: BarChart3,
                title: "Risk Assessment and Business Impact Analysis (BIA)",
                desc: "The standard emphasizes the importance of conducting a risk assessment and Business Impact Analysis (BIA) to identify the critical business functions and assess the impact of potential disruptions on operations.",
              },
              {
                icon: Target,
                title: "Business Continuity Plans (BCPs)",
                desc: "ISO 22301 requires organizations to develop, implement, and regularly update comprehensive Business Continuity Plans (BCPs). These plans outline how the organization will maintain or resume critical operations in the event of a disruption, ensuring the continued delivery of key services or products.",
              },
              {
                icon: Users,
                title: "Incident Response and Crisis Management",
                desc: "The standard provides guidelines on incident response and crisis management, ensuring that organizations are able to respond effectively to disruptions, minimizing the impact on operations, employees, customers, and stakeholders.",
              },
              {
                icon: TrendingUp,
                title: "Testing, Monitoring, and Continuous Improvement",
                desc: "ISO 22301 requires regular testing of business continuity plans to ensure their effectiveness. The BCMS must be regularly monitored, reviewed, and updated to adapt to evolving risks, ensuring continuous improvement and resilience.",
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-gray-50 hover:bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Matters */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why ISO 22301 Certification Matters for Your Business
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Enhanced Organizational Resilience — ISO 22301 helps organizations build a resilient business by ensuring that essential functions can continue or be restored swiftly in the event of a disruption, reducing the impact on operations, revenue, and reputation.",
                "Improved Risk Management — The standard provides a structured approach to identifying and assessing risks that could impact business continuity, helping businesses take proactive measures to mitigate potential disruptions.",
                "Compliance with Regulatory Requirements — Achieving ISO 22301 certification ensures that your organization meets legal, regulatory, and contractual requirements related to business continuity and disaster recovery, reducing the risk of non-compliance penalties.",
                "Customer Confidence — Certification demonstrates to customers, partners, and stakeholders that your organization is committed to maintaining business operations during crises and is prepared to recover quickly, fostering trust and confidence in your organization’s ability to manage disruptions.",
                "Competitive Advantage — ISO 22301 certification provides a competitive edge by signaling to clients, investors, and stakeholders that your organization has implemented a robust business continuity management system (BCMS), making you a more attractive partner in industries that prioritize resilience.",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white border border-gray-100"
                >
                  <ArrowRight
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Does Include */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Does ISO 22301 Certification Include?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              ISO 22301 outlines the requirements for developing a comprehensive
              Business Continuity Management System (BCMS), which includes the
              following key components:
            </p>
            <div className="space-y-4">
              {[
                "1. Context of the Organization — Define the organization’s context, including its internal and external factors, legal and regulatory obligations, and the needs and expectations of stakeholders, which could affect business continuity.",
                "2. Leadership and Commitment — Senior management must demonstrate leadership and commitment by establishing a business continuity policy, allocating resources, and ensuring that business continuity is aligned with the organization’s strategic objectives.",
                "3. Risk Assessment and Business Impact Analysis (BIA) — Conduct a risk assessment and Business Impact Analysis (BIA) to identify critical business functions, assess the potential impact of disruptions, and evaluate the resources required to maintain or restore these functions.",
                "4. Business Continuity Strategies and Objectives — Develop strategies and objectives to ensure that critical business functions can continue or be restored in the event of a disruption. This includes defining resource requirements, operational strategies, and service levels.",
                "5. Business Continuity Plans (BCPs) — Develop and implement Business Continuity Plans (BCPs) that provide clear instructions on how to respond to different types of disruptions, including emergency response, crisis management, and disaster recovery.",
                "6. Testing, Exercising, and Training — Regularly test and exercise the business continuity plans to ensure they are effective and that employees are trained to respond appropriately during a disruption.",
                "7. Monitoring, Reviewing, and Continuous Improvement — Continuously monitor and review the BCMS to ensure its effectiveness and make improvements as needed. This includes conducting management reviews, implementing corrective actions, and adapting to changes in the organization’s risk landscape.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Benefits of ISO 22301 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                "Improved Business Continuity",
                "Enhanced Risk Management",
                "Regulatory Compliance",
                "Increased Stakeholder Confidence",
                "Operational Efficiency",
              ].map((adv, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <CheckCircle
                    size={20}
                    className="text-[var(--bg-primary)] mb-3"
                  />
                  <p className="text-gray-700 font-medium text-sm">{adv}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to Achieve */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How to Achieve ISO 22301 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-700 font-semibold text-lg mb-6">
              Achieving ISO 22301 certification typically involves the following
              steps:
            </p>
            <div className="space-y-4">
              {[
                "1. Establish a Business Continuity Framework — Define the scope and objectives of your Business Continuity Management System (BCMS), ensuring alignment with your organization’s strategic goals and risk management strategy.",
                "2. Conduct a Risk Assessment and Business Impact Analysis — Identify and assess the potential risks to business continuity and conduct a Business Impact Analysis (BIA) to evaluate the impact of disruptions on critical functions.",
                "3. Develop Business Continuity Plans (BCPs) — Create Business Continuity Plans (BCPs) for critical business functions, outlining procedures for managing disruptions, including crisis management, emergency response, and disaster recovery.",
                "4. Test and Exercise the Plans — Regularly test and exercise your business continuity plans to ensure they are effective and that your team is prepared to respond to disruptions.",
                "5. Internal Audits and Documentation — Conduct internal audits and ensure that documentation is in place to demonstrate compliance with ISO 22301. Maintain records of risk assessments, impact analyses, and plan testing.",
                "6. Third-Party Certification Audit — Engage an accredited certification body to conduct a third-party audit to assess the effectiveness of your BCMS and verify compliance with the ISO 22301 standard.",
                "7. Ongoing Monitoring and Improvement — Continuously monitor the performance of your BCMS, review its effectiveness, and implement improvements based on audits, testing, and lessons learned.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose MBA Solutions for ISO 22301 Certification?
            </h2>
            <div className="w-20 h-1 bg-orange-500 rounded-full mx-auto mb-6" />
            <p className="text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto">
              MBA Solutions Pvt. Ltd. provides expert ISO 22301 certification
              consultancy services, helping businesses establish, implement, and
              maintain a comprehensive Business Continuity Management System
              (BCMS) that ensures organizational resilience and risk management.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-10"
          >
            <p className="text-gray-300 font-semibold text-lg text-center mb-6">
              Our services include:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "ISO 22301 Gap Analysis and Risk Assessment",
                "Business Continuity Strategy and Plan Development",
                "Crisis Management and Disaster Recovery Planning",
                "Internal Audit and Pre-certification Support",
                "Third-Party Certification Assistance",
                "Continuous Monitoring and Improvement",
              ].map((service, i) => (
                <span
                  key={i}
                  className="bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-medium"
                >
                  {service}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 text-center"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6">
              <a
                href="mailto:atif@mbasolutions.co"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <Mail size={20} />
                <span>atif@mbasolutions.co</span>
              </a>
              <a
                href="https://www.mbasolutions.co"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <ExternalLink size={20} />
                <span>www.mbasolutions.co</span>
              </a>
            </div>
            <p className="text-gray-300 text-base leading-relaxed max-w-3xl mx-auto">
              We help businesses achieve ISO 22301 certification, ensuring
              business continuity, risk management, and resilience in the face
              of disruptions.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ISO22301_Details;
